import React, { useEffect, useState } from 'react';
import { FieldGroup } from '@ver-uds/react';
import { useAuthState } from '@vis-auth/react';
import { Alert, Form } from '@ver-uds/uswds-react';
import { FormProvider, useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { Account } from 'eve-account-client';
import FullWidthLayout from '../layouts/FullWidthLayout';
import { useClosedReasonsQuery } from '../services';
import DUNSField from './DUNSField';
import HelpModal, { HelpModalProps } from './HelpModal';
import AlertConstants from '../constants/AlertConstants';
import HelpTextConstants from '../constants/HelpTextConstants';
import TerminationRequestFeedback from './TerminationRequestFeedback';

const pageTitle = 'Terminate Company Account';

export type TerminationRequestProps = {
  accountData: Account;
  terminationFormValues: FormValues;
  goToReviewPage: (terminationFormValues: FormValues) => void;
};

export type FormValues = {
  closedReasonCode: string;
  closedReasonText: string;
  childClosedReasonCode: string;
  dunsNumber: string;
};

const TerminationRequest = ({
  accountData,
  terminationFormValues,
  goToReviewPage,
}: TerminationRequestProps): JSX.Element => {
  const { user } = useAuthState();
  // eslint-disable-next-line camelcase
  const accountId = user?.account_id as string;

  const companyInfoData = accountData?.companyInfo;

  const { data: closedReasons } = useClosedReasonsQuery(accountData?.type);

  function initializeNode(): React.ReactNode {
    return <></>;
  }

  const [helpModalIsOpen, setHelpModalIsOpen] = useState(false);
  const [terminationAllowed, setTerminationAllowed] = useState(true);
  const [getModalContent, setModalContent] = useState({ header: '', body: initializeNode() });

  const openModal = (content: HelpModalProps['modalContent']) => {
    setModalContent(content);
    setHelpModalIsOpen(true);
  };

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const methods = useForm<FormValues>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: terminationFormValues || {
      dunsNumber: companyInfoData?.dunsNumber,
      closedReasonCode: undefined,
      closedReasonText: undefined,
      childClosedReasonCode: undefined,
    },
  });

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { watch, handleSubmit } = methods;

  const { closedReasonCode, closedReasonText, childClosedReasonCode, dunsNumber } = watch();

  const todayDatePretty = format(new Date(), 'E, MMM d, yyyy');

  // Find selected reasons list
  const matchingReasonsList = closedReasons
    ?.filter((reason) => reason.code === (childClosedReasonCode ?? closedReasonCode))
    .flatMap((reason) => ({
      terminationNotAllowed: reason.terminationNotAllowed,
      terminationNotAllowedReason: reason.terminationNotAllowedReason,
    }));

  const selectedReason = matchingReasonsList && matchingReasonsList.length > 0 ? matchingReasonsList[0] : null;

  const onFormSubmit = handleSubmit(() => {
    if (selectedReason && selectedReason.terminationNotAllowed) {
      // If termination is not required, display banner
      setTerminationAllowed(false);
    } else {
      // Continue to review page
      const formValues: FormValues = {
        closedReasonCode,
        closedReasonText,
        childClosedReasonCode,
        dunsNumber,
      };
      goToReviewPage(formValues);
    }
  });

  const descriptionText = HelpTextConstants.TERMINATION_REQUEST.replace(
    '{{ companyText }}',
    companyInfoData?.companyName ?? 'this company',
  );

  // Reset termination required if reason is changed
  useEffect(() => {
    setTerminationAllowed(true);
  }, [closedReasonCode, childClosedReasonCode]);

  return (
    <FullWidthLayout title={pageTitle}>
      <section className="padding-y-4">
        <h1>{pageTitle}</h1>

        {terminationAllowed && (
          <Alert className="margin-top-4" status="warning" slim>
            <Alert.Text>{AlertConstants.TERMINATION_REQUEST}</Alert.Text>
          </Alert>
        )}
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <FormProvider {...methods}>
          <Form className="margin-top-3 margin-bottom-3 maxw-none" onSubmit={onFormSubmit} noValidate>
            <FieldGroup className="grid-row grid-gap margin-bottom-neg-3">
              <FieldGroup.Field className="tablet:grid-col-12" name="Company Name">
                {companyInfoData?.companyName ?? '---'}
              </FieldGroup.Field>
              <FieldGroup.Field className="tablet:grid-col-12" name="Company ID">
                {accountId}
              </FieldGroup.Field>
            </FieldGroup>

            {companyInfoData?.dunsNumber && (
              <FieldGroup className="grid-row grid-gap margin-bottom-neg-3">
                <FieldGroup.Field className="tablet:grid-col-12" name="DUNS Number">
                  {companyInfoData?.dunsNumber}
                </FieldGroup.Field>
              </FieldGroup>
            )}

            {!companyInfoData?.dunsNumber && <DUNSField openModal={openModal} />}

            <FieldGroup>
              <FieldGroup.Field className="tablet:grid-col-12 margin-top-3" name="Termination Request Date">
                {todayDatePretty}
              </FieldGroup.Field>
            </FieldGroup>

            <TerminationRequestFeedback
              terminationAllowed={terminationAllowed}
              terminationNotAllowedReason={selectedReason?.terminationNotAllowedReason}
              feedbackText={descriptionText}
              closedReasons={closedReasons}
            />
          </Form>
        </FormProvider>
      </section>
      <HelpModal
        modalContent={getModalContent}
        open={helpModalIsOpen}
        close={(): void => {
          setHelpModalIsOpen(false);
        }}
      />
    </FullWidthLayout>
  );
};

export default TerminationRequest;
